import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, LayoutGrid, Layers, LogIn, LogOut, PanelLeftClose, PanelLeftOpen, Settings } from 'lucide-react';
import './App.css';
import { AUTH_CONFIG } from './auth/authConfig';
import RequireAuth from './auth/RequireAuth';
import { consumeEntraReturnTo, useAuthAccess } from './auth/AuthAccessProvider';
import lbsLogo from './assets/lbs-logo.jpg';
import QueryInterface from './components/QueryInterface';
import CourseList from './components/CourseList';
import PathwayBuilder from './components/PathwayBuilder';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import SettingsPage from './components/SettingsPage';

function getAuthCallbackParams(location) {
  const searchParams = new URLSearchParams(location.search);
  const hashValue = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  const hashParams = new URLSearchParams(hashValue);

  return {
    description: searchParams.get('error_description') || hashParams.get('error_description') || '',
    error: searchParams.get('error') || hashParams.get('error') || '',
    hasCallbackCode: (searchParams.has('state') && searchParams.has('code'))
      || (hashParams.has('state') && hashParams.has('code')),
    hasCallbackError: searchParams.has('error') || hashParams.has('error')
  };
}

function AppLoadingState() {
  return (
    <div className="app-shell">
      <main className="main-content schema-scroll">
        <section className="page">
          <div className="page-header">
            <div className="section-label">Loading</div>
            <h1>Checking session</h1>
          </div>
        </section>
      </main>
    </div>
  );
}

function AppAuthErrorState({ description, error, onLoginAgain, onLogout }) {
  return (
    <div className="app-shell">
      <main className="main-content schema-scroll">
        <section className="page">
          <div className="page-header">
            <div className="section-label">Sign-in issue</div>
            <h1>Microsoft could not complete sign-in</h1>
            <p className="assistant-info">
              {description || error || 'The sign-in provider returned an error before ExecEye could create a session.'}
            </p>
            <div className="action-row">
              <button type="button" className="btn-primary" onClick={onLoginAgain}>
                Try again
              </button>
              <button type="button" className="btn-secondary" onClick={onLogout}>
                Clear session
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const {
    error,
    hasPermission,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout,
    isAuthorizing,
    user
  } = useAuthAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = !isAuthorizing && hasPermission(AUTH_CONFIG.permissions.admin);
  const {
    description: authCallbackDescription,
    error: authCallbackError,
    hasCallbackCode: isAuthCallback,
    hasCallbackError: hasAuthCallbackError
  } = getAuthCallbackParams(location);
  const persistentPages = [
    {
      path: '/courses',
      permission: AUTH_CONFIG.permissions.user,
      element: <CourseList />
    },
    {
      path: '/queries',
      permission: AUTH_CONFIG.permissions.user,
      element: <QueryInterface />
    },
    {
      path: '/pathway',
      permission: AUTH_CONFIG.permissions.user,
      element: <PathwayBuilder />
    },
    {
      path: '/analytics',
      permission: AUTH_CONFIG.permissions.admin,
      element: <AnalyticsDashboard />
    },
    {
      path: '/settings',
      permission: AUTH_CONFIG.permissions.admin,
      element: <SettingsPage />
    }
  ];
  const defaultPath = '/courses';
  const activePage = persistentPages.find((page) => (
    location.pathname === page.path || location.pathname.startsWith(`${page.path}/`)
  ));
  const visiblePage = activePage || persistentPages[0];
  const visiblePath = visiblePage.path;

  const navItems = [
    { to: '/courses', label: 'Programmes', icon: LayoutGrid, permission: AUTH_CONFIG.permissions.user },
    { to: '/queries', label: 'Query', icon: Search, permission: AUTH_CONFIG.permissions.user },
    { to: '/pathway', label: 'Pathways', icon: Layers, permission: AUTH_CONFIG.permissions.user },
    { to: '/settings', label: 'Settings', icon: Settings, permission: AUTH_CONFIG.permissions.admin }
  ].filter((item) => !item.permission || (!isAuthorizing && hasPermission(item.permission)));

  const handleLogout = () => logout();

  useEffect(() => {
    if (hasAuthCallbackError) return;
    if (isAuthCallback) return;
    if (location.pathname === '/') {
      const savedReturnTo = isAuthenticated ? consumeEntraReturnTo() : '';
      const targetPath = savedReturnTo.startsWith('/') ? savedReturnTo : defaultPath;
      navigate(targetPath, { replace: true });
      return;
    }

    if (!activePage) {
      navigate(defaultPath, { replace: true });
    }
  }, [activePage, hasAuthCallbackError, isAuthCallback, isAuthenticated, location.pathname, navigate]);

  const handleNavigate = (to) => {
    if (location.pathname === to) {
      setMobileSidebarOpen(false);
      return;
    }

    setMobileSidebarOpen(false);
    if (location.pathname.startsWith('/pathway') && window.__execEyeHasUnsavedPathway && !window.confirm('Leave without saving this pathway? Unsaved changes will be lost.')) {
      return;
    }

    navigate(to);
  };

  if (hasAuthCallbackError) {
    return (
      <AppAuthErrorState
        description={authCallbackDescription}
        error={authCallbackError}
        onLoginAgain={() => loginWithRedirect()}
        onLogout={handleLogout}
      />
    );
  }

  if (isLoading) {
    return <AppLoadingState />;
  }

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <button className="mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)} aria-label="Open navigation menu">
        <Menu size={18} />
      </button>

      {mobileSidebarOpen && <div className="mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <button className="mobile-close-btn" onClick={() => setMobileSidebarOpen(false)} aria-label="Close navigation menu">
            <X size={18} />
          </button>
          <div className="logo-block">
            <img src={lbsLogo} alt="London Business School" className="lbs-logo-img" />
            {!sidebarCollapsed && <div className="app-name">ExecEye</div>}
            <button
              className="collapse-btn collapse-btn-top"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              aria-label={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </div>
        </div>

        <div className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

            return (
              <button
                key={item.to}
                type="button"
                className={`nav-link ${isActive ? 'active' : ''}`}
                onClick={() => handleNavigate(item.to)}
              >
                <Icon size={18} />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </div>

        <div className="sidebar-bottom">
          <div className="auth-panel">
            {isLoading && <div className="auth-status">Checking session...</div>}
            {error && <div className="auth-error">Auth error: {error.message}</div>}
            {!isLoading && isAuthenticated && (
              <>
                {!sidebarCollapsed && (
                  <div className="auth-user">
                    <strong>{user?.name || user?.email}{isAdmin ? ' (Admin)' : ''}</strong>
                  </div>
                )}
                <button className="auth-btn" onClick={handleLogout} aria-label="Log out">
                  <LogOut size={16} />
                  {!sidebarCollapsed && <span>Logout</span>}
                </button>
              </>
            )}
            {!isLoading && !isAuthenticated && (
              <button className="auth-btn auth-btn-primary" onClick={() => loginWithRedirect()} aria-label="Log in">
                <LogIn size={16} />
                {!sidebarCollapsed && <span>Login</span>}
              </button>
            )}
          </div>
        </div>
      </aside>

      <main className="main-content schema-scroll">
        {!isAuthenticated ? (
          <div className="persistent-page-panel">
            <RequireAuth permission={visiblePage.permission}>
              {visiblePage.element}
            </RequireAuth>
          </div>
        ) : (
          <div className="persistent-route-stack">
            {persistentPages.map((page) => {
              const isVisible = visiblePath === page.path;

              return (
                <div
                  key={page.path}
                  className="persistent-page-panel"
                  hidden={!isVisible}
                  aria-hidden={!isVisible}
                >
                  <RequireAuth permission={page.permission}>
                    {page.element}
                  </RequireAuth>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return <AppLayout />;
}

export default App;
